import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {tickStorage} from "@dashboard/contexts/tickStore";
import {calculateLatestPFValues, getLastClosePrice, getLastPrice} from "@portfolio/utils/PFService";
import { apiFetch} from "apiClient";
import { apiFetch as fetch } from "apiClient";

export const generateExportData = (stocks, pfQuotes, activeTab) => {
    const headerRow = [
        "Stocks",
        "Today's Gain", "Today's Gain %",
        "Overall Gain", "Overall Gain %",
        "Current Value",
        "Quantity", "Avg Price",
        "Invested",
        "Price"
    ];

    let totalAmountInvested = 0;
    let totalTodayGain = 0;
    let totalOverallGain = 0;
    let totalCurrentValue = 0;

    const bodyRows = stocks.map(stock => {
        const values = calculateLatestPFValues(stock, pfQuotes);
        if (!values) return null;

        const {
            lastPrice,
            todayGain,
            overallGain,
            currentValue
        } = values;

        totalAmountInvested += stock.amountInvested || 0;
        totalTodayGain += todayGain || 0;
        totalOverallGain += overallGain || 0;
        totalCurrentValue += currentValue || 0;

        return [
            stock.instrumentEntity.tradingsymbol,
            todayGain.toFixed(2), ((todayGain / (stock.amountInvested || 1)) * 100).toFixed(2) + "%",
            overallGain.toFixed(2), ((overallGain / (stock.amountInvested || 1)) * 100).toFixed(2) + "%",
            currentValue.toFixed(2),
            stock.netQuantity,
            stock.weightedAvgPrice?.toFixed(2),
            stock.amountInvested?.toFixed(2),
            lastPrice.toFixed(2)
        ];
    }).filter(Boolean);

    const totalTodayGainPercentage = totalAmountInvested > 0 ? ((totalTodayGain / totalAmountInvested) * 100).toFixed(2) : "0.00";
    const totalOverallGainPercentage = totalAmountInvested > 0 ? ((totalOverallGain / totalAmountInvested) * 100).toFixed(2) : "0.00";

    const footerRow = [
        "Total",
        totalTodayGain.toFixed(2), totalTodayGainPercentage + "%",
        totalOverallGain.toFixed(2), totalOverallGainPercentage + "%",
        totalCurrentValue.toFixed(2),
        "", "",
        totalAmountInvested.toFixed(2),
        ""
    ];

    return [headerRow, ...bodyRows, footerRow];
};


export const exportToCSV = (stocks, pfQuotes, activeTab) => {
    const data = generateExportData(stocks, pfQuotes);
    const csvContent = data.map(row => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `Portfolio_${activeTab}.csv`);
};

export const exportToExcel = (stocks, totals, activeTab) => {
    const data = generateExportData(stocks, totals);
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Portfolio");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `Portfolio_${activeTab}.xlsx`);
};
function toDec(x) {
    if (x === null || x === undefined) return null;
    let s = String(x).trim();
    const neg = /^\(.*\)$/.test(s);           // (123.45) style negatives
    s = s.replace(/[(),%]/g, '').replace(/,/g, '');
    const n = parseFloat(s);
    return Number.isFinite(n) ? (neg ? -n : n) : null;
}

function toInt(x) {
    if (x === null || x === undefined) return null;
    let s = String(x).trim().replace(/,/g, '');
    const neg = /^\(.*\)$/.test(s);
    s = s.replace(/[()]/g, '');
    const n = parseInt(s, 10);
    return Number.isFinite(n) ? (neg ? -n : n) : null;
}

export async function syncPortfolioExport(stocks, pfQuotes, activeTab) {
    const rows = generatePortFolioSyncData(stocks, pfQuotes, activeTab); // AoA: [header, ...rows, footer]

    const asOf = new Date().toISOString();
    const tab = String(activeTab ?? "default");

    const body = rows.map(r => ({
        stockSymbol: r[0],
        isin: r[1],                       // 👈 new field at second place
        todaysGain: toDec(r[2]),
        todaysGainPct: toDec(r[3]),
        overallGain: toDec(r[4]),
        overallGainPct: toDec(r[5]),
        currentValue: toDec(r[6]),
        quantity: toInt(r[7]),
        avgPrice: toDec(r[8]),
        invested: toDec(r[9]),
        price: toDec(r[10]),
        asOf,
        activeTab: tab
    }));
    console.log("Portfolio save called for ", tab);

    await fetch("/api/portfolio-export/sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body)
    });
}

export const generatePortFolioSyncData = (stocks, pfQuotes, activeTab) => {
    let totalAmountInvested = 0;
    let totalTodayGain = 0;
    let totalOverallGain = 0;
    let totalCurrentValue = 0;

    const bodyRows = stocks.map(stock => {
        const values = calculateLatestPFValues(stock, pfQuotes);
        if (!values) return null;

        const {
            lastPrice,
            todayGain,
            overallGain,
            currentValue
        } = values;

        totalAmountInvested += stock.amountInvested || 0;
        totalTodayGain += todayGain || 0;
        totalOverallGain += overallGain || 0;
        totalCurrentValue += currentValue || 0;

        return [
            stock.instrumentEntity.tradingsymbol,
            stock.isin,
            todayGain.toFixed(2), ((todayGain / (stock.amountInvested || 1)) * 100).toFixed(2) + "%",
            overallGain.toFixed(2), ((overallGain / (stock.amountInvested || 1)) * 100).toFixed(2) + "%",
            currentValue.toFixed(2),
            stock.netQuantity,
            stock.weightedAvgPrice?.toFixed(2),
            stock.amountInvested?.toFixed(2),
            lastPrice.toFixed(2)
        ];
    }).filter(Boolean);

    return [...bodyRows];
};

